import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { Error as FieldError, InfoIcon, RequiredStar, Tooltip } from '@pbk-web/ui';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';

import { Toolbar } from '../features/toolbar/Toolbar';
import { ContentArea } from './ContentArea';
import { useEditorChrome } from './context/EditorChromeContext';
import { useEditorProps, useEditorSlot } from './context/EditorPropsContext';
import { useFeatureEnabled } from './context/FeaturesContext';

/**
 * The visible editor, matching Froala's `.fr-box`: toolbar docked inside a 2px rounded border.
 * - The border is black at rest, accent while active, red with an error, faded when disabled.
 * - Around the frame, top to bottom: label, `before` slot, frame, `after` slot, error, dynamic-data hint.
 * - `data-*` attributes mirror Froala's, so both editors can be targeted the same way.
 */
export const EditorShell = () => {
  const [editor] = useLexicalComposerContext();
  const { disabled, label, required, helpText, error, errorState, toolbarPosition = 'top' } = useEditorProps();
  const { focused, wrapperRef, onFocusIn } = useEditorChrome();
  const hasToolbar = useFeatureEnabled('richText');
  const hasDynamicData = useFeatureEnabled('dynamicData');
  const before = useEditorSlot('before');
  const after = useEditorSlot('after');
  const { t } = useTranslation('common');
  const id = useId();
  const ids = { label: label ? `${id}-label` : undefined, error: error ? `${id}-error` : undefined };

  const borderColour = disabled ? 'border-greyscale-15' : errorState || error ? 'border-red-500' : focused ? 'border-accent-1-100' : 'border-black';
  const toolbar = hasToolbar && <Toolbar />;

  return (
    <div
      ref={wrapperRef}
      onFocus={onFocusIn}
      data-lexical-focused={focused}
      data-disabled={disabled ? 'true' : 'false'}
      className={disabled ? 'cursor-not-allowed opacity-50' : undefined}
    >
      {label && (
        <div className="mb-1 flex items-center gap-2">
          {/* The content is a contenteditable, not a labelable element, so the click is forwarded by hand */}
          <label id={ids.label} onClick={() => editor.focus()}>
            {required && <RequiredStar />}
            {label}
          </label>
          {!!helpText && (
            <Tooltip text={helpText}>
              {(tooltip) => (
                <div {...tooltip}>
                  <InfoIcon className="h-4 w-4" />
                </div>
              )}
            </Tooltip>
          )}
        </div>
      )}

      {before()}

      <div className={`flex flex-col rounded-lg border-2 bg-white ${borderColour}`}>
        {toolbarPosition === 'top' && toolbar}
        <ContentArea ids={ids} />
        {toolbarPosition === 'bottom' && toolbar}
      </div>

      {after()}

      {error && (
        <div id={ids.error}>
          <FieldError>{error}</FieldError>
        </div>
      )}

      {/* Froala's host renders the same hint below the box, and only while the field is active */}
      {hasDynamicData && focused && <p className="text-pbk-12 text-text-2 px-1 pt-1">{t('editor.dynamic-data-hint')}</p>}
    </div>
  );
};
